

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import logoIcon from "../assets/logoCf.png";
import { Phone, Menu, X } from "lucide-react";
import "./Navbar.css";

/**
 * Robust sticky->fixed navbar using IntersectionObserver.
 * - Observes a sentinel element that sits immediately before the nav in the DOM.
 * - Uses nearest scrollable ancestor as observer root (falls back to viewport).
 * - When sentinel is NOT visible, we add .fixed-nav to make navbar fixed.
 */

const getScrollParent = (node) => {
  if (!node) return null;
  let parent = node.parentElement;
  while (parent) {
    const style = getComputedStyle(parent);
    const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;
    if (/(auto|scroll|overlay)/.test(overflow)) return parent;
    parent = parent.parentElement;
  }
  return null;
};

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("home");
  const [fixed, setFixed] = useState(false);
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    // determine the scroll container (root) or null for viewport
    const scrollParent = getScrollParent(sentinel);
    const root = scrollParent || null;

    // clean up any previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // create observer: when sentinel is NOT intersecting -> make fixed
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // entry.isIntersecting === false -> sentinel left view -> fix navbar
          setFixed(!entry.isIntersecting);
        });
      },
      {
        root,
        threshold: 0,
        rootMargin: "0px",
      }
    );

    io.observe(sentinel);
    observerRef.current = io;

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []); // run once on mount

  const handleNavClick = (id, path) => {
    setActive(id);
    setMenuOpen(false);
    navigate(path);
   window.scrollTo(0, 0);

  };
 


  const navItems = [
    { id: "home", label: "Home", path: "/" },
    { id: "about", label: "About Us", path: "/about" },
    { id: "courses", label: "Explore Courses", path: "/allcourses" },
    { id: "blogs", label: "Blogs", path: "/blogs" },
    { id: "contact", label: "Contact Us", path: "/contact" },
  ];

  return (
    <>
      {/* sentinel placed immediately before nav to detect when top-of-page is scrolled away */}
      <div ref={sentinelRef} className="nav-sentinel" />

      {/* placeholder prevents layout jump when we switch to fixed */}
      {fixed ? <div className="nav-placeholder" aria-hidden="true" /> : null}

      <nav className={`navbar ${fixed ? "fixed-nav" : ""}`}>
        <div className="nav-container">
          <div className="logo-flex" onClick={() => handleNavClick("home", "/")}>
            <img src={logoIcon} alt="Codex Future" className="logo-img" />
            <div className="logo-text">
              <h2>Codex Future</h2>
              <p>IT Training & Placement</p>
            </div>
          </div>

          <ul className={`nav-links ${menuOpen ? "active" : ""}`}>
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  className={active === item.id ? "active-link" : ""}
                  onClick={() => handleNavClick(item.id, item.path)}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          <a href="tel:+919303715744" className="nav-phone">
            <div className="phone-icon">
              <Phone size={18} />
            </div>
            <span className="phone-btn">Call Now</span>
          </a>

          <div className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;